import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, MailCheck, X } from 'lucide-react';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { Button, Progress } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  markFolderAsReadBatch,
  type MarkFolderAsReadFilters,
  type MarkFolderAsReadBatchResult,
} from '../useData';

export interface MarkFolderAsReadTarget {
  folderName: string;
  unreadEstimate: number;
  filters: MarkFolderAsReadFilters;
}

interface MarkFolderAsReadModalProps {
  isOpen: boolean;
  target: MarkFolderAsReadTarget | null;
  onClose: () => void;
  onComplete: (result: { marked: number; cancelled: boolean; total: number }) => void;
}

type Phase = 'idle' | 'running' | 'done' | 'cancelled';

const BATCH_TAKE = 500;

const MarkFolderAsReadModal: React.FC<MarkFolderAsReadModalProps> = ({
  isOpen,
  target,
  onClose,
  onComplete,
}) => {
  const { colors } = useTheme();
  const [phase, setPhase] = useState<Phase>('idle');
  const [doneCount, setDoneCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef(false);
  const runningRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !target) return;
    setPhase('idle');
    setDoneCount(0);
    setTotalCount(target.unreadEstimate);
    setError(null);
    cancelRef.current = false;
    runningRef.current = false;
  }, [isOpen, target]);

  const percent =
    totalCount > 0
      ? Math.min(100, Math.round((doneCount / totalCount) * 100))
      : phase === 'done'
        ? 100
        : 0;

  const handleClose = () => {
    if (phase === 'running') {
      cancelRef.current = true;
      return;
    }
    onClose();
  };

  const handleStart = async () => {
    if (!target || runningRef.current) return;
    runningRef.current = true;
    cancelRef.current = false;
    setPhase('running');
    setError(null);

    let markedTotal = 0;
    let progressTotal = 0;
    let cancelled = false;

    try {
      for (;;) {
        if (cancelRef.current) {
          cancelled = true;
          await markFolderAsReadBatch({ ...target.filters, take: 1, finalize: true });
          break;
        }

        const res: MarkFolderAsReadBatchResult = await markFolderAsReadBatch({
          ...target.filters,
          take: BATCH_TAKE,
        });

        if (progressTotal <= 0) {
          progressTotal = Math.max(res.totalUnread, target.unreadEstimate, res.marked);
          setTotalCount(progressTotal);
        }

        markedTotal += res.marked;
        setDoneCount(markedTotal);

        if (res.done || res.marked === 0) {
          break;
        }
      }

      if (cancelled) {
        setPhase('cancelled');
        onComplete({ marked: markedTotal, cancelled: true, total: progressTotal });
      } else {
        setPhase('done');
        onComplete({ marked: markedTotal, cancelled: false, total: progressTotal });
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || (err instanceof Error ? err.message : 'Failed to mark folder as read'));
      setPhase('idle');
      try {
        await markFolderAsReadBatch({ ...target.filters, take: 1, finalize: true });
      } catch {
        // ignore finalize errors
      }
    } finally {
      runningRef.current = false;
    }
  };

  if (!isOpen || !target) return null;

  const progressVariant =
    phase === 'done' ? 'success' : phase === 'cancelled' ? 'warning' : 'default';

  const headline =
    phase === 'idle'
      ? 'Mark all unread messages in this folder as read.'
      : phase === 'running'
        ? 'Working through unread messages…'
        : phase === 'cancelled'
          ? 'Stopped — already marked messages stay read.'
          : doneCount === 0
            ? 'No unread messages in this folder.'
            : 'All selected unread messages are marked as read.';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      closeOnEscape={phase !== 'running'}
    >
      <ModalHeader>
        <ModalTitle>Mark folder as read</ModalTitle>
        <button
          type="button"
          onClick={handleClose}
          disabled={phase === 'running'}
          className={cn(
            'rounded-md p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            colors.textMuted,
            colors.interactiveHover,
          )}
          aria-label="Close"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          <p className={cn('text-sm leading-relaxed', colors.textSecondary)}>{headline}</p>

          <div
            className={cn(
              'rounded border p-3 text-sm',
              colors.border,
              colors.surfaceHover,
              colors.textSecondary,
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <span className={cn('font-medium', colors.text)}>Folder</span>
              <span className="min-w-0 truncate text-right" title={target.folderName}>
                {target.folderName}
              </span>
            </div>
            {phase === 'idle' && totalCount > 0 && (
              <div className="mt-2 flex items-start justify-between gap-4">
                <span className={cn('font-medium', colors.text)}>Unread messages</span>
                <span className="tabular-nums">{totalCount.toLocaleString()}</span>
              </div>
            )}
          </div>

          <p className={cn('text-xs leading-relaxed', colors.textMuted)}>
            This applies only to your account. Other users keep their own read state.
          </p>

          {(phase === 'running' || phase === 'done' || phase === 'cancelled') && (
            <div className="space-y-2.5">
              <div className="flex items-end justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2.5">
                  {phase === 'running' ? (
                    <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-indigo-500" aria-hidden />
                  ) : (
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  )}
                  <div>
                  <p className={cn('text-sm font-medium', colors.text)}>
                    {phase === 'running'
                      ? 'Progress'
                      : phase === 'cancelled'
                        ? 'Partial result'
                        : 'Complete'}
                  </p>
                  <p className={cn('text-xs tabular-nums mt-0.5', colors.textMuted)}>
                    {doneCount.toLocaleString()}
                    {totalCount > 0 ? ` / ${totalCount.toLocaleString()}` : ''} marked
                  </p>
                  </div>
                </div>
                <span
                  className={cn(
                    'text-lg font-semibold tabular-nums leading-none',
                    phase === 'done'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : phase === 'cancelled'
                        ? 'text-amber-600 dark:text-amber-400'
                        : colors.text,
                  )}
                >
                  {percent}%
                </span>
              </div>
              <Progress value={percent} max={100} size="lg" variant={progressVariant} />
              {phase === 'running' && (
                <p className={cn('text-[11px]', colors.textMuted)}>
                  Keep this tab open. You can cancel after the current batch finishes.
                </p>
              )}
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-900 bg-red-50/80 dark:bg-red-950/30 px-3 py-2">
              {error}
            </p>
          )}
        </div>
      </ModalContent>
      <ModalFooter className="justify-end gap-2">
        {phase === 'idle' && (
          <>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleStart()}
              className="gap-1.5"
            >
              <MailCheck className="h-4 w-4" strokeWidth={2} aria-hidden />
              Start
            </Button>
          </>
        )}
        {phase === 'running' && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              cancelRef.current = true;
            }}
          >
            Cancel
          </Button>
        )}
        {(phase === 'done' || phase === 'cancelled') && (
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default MarkFolderAsReadModal;
